import useTranslator from "../../hooks/useTranslator";
import { useLocation, useNavigate } from "react-router-dom";
import { sendUniversalMail } from "../../services/sharedService";
import { useContext, useState } from "react";
import ApplicationContext from "../../context/application/ApplicationContext";
import AuthContext from "../../context/auth/AuthContext";
import TenantContext from "../../context/tenant/TenantContext";
import DeleteConfiration from "./DeleteConfiration";
import { getGuarantorStatus, haveValue, toast } from "../../helper/common";
import { Icon } from "../design";
import {
  GuarantorCard,
  GuarantorMenu,
  GuarantorStatusBadge,
} from "./GuarantorCardParts";

const EditConfirmationPrompt = DeleteConfiration;

const PendingGuarantorPills = ({
  userData,
  llShowOnly,
  isDropdown,
  setIsDropdown,
  readOnly = false,
  onClose,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const [resendRequestLoading, setResendRequestLoading] = useState(false);
  const { loadUser } = useContext(AuthContext);
  const { removeGuarantor, updateBasicGuarantorAccount } =
    useContext(TenantContext);
  const { reloadApplications } = useContext(ApplicationContext);
  const [showDeleteConfirmationPrompt, setShowDeleteConfirmationPrompt] =
    useState(false);
  const [showEditConfirmationPrompt, setShowEditConfirmationPrompt] =
    useState(false);
  const [deleteGuarantorLoading, setDeleteGuarantorLoading] = useState(false);
  const [editGuarantorLoading, setEditGuarantorLoading] = useState(false);
  const guarantor = userData?.guarantor_id;
  const hasIdentityDetails = Boolean(
    guarantor?.dni_nie || guarantor?.passport_id || guarantor?.identity_number
  );

  const handleCompleteOnboarding = () => {
    navigate(`/guarantor/onboarding?guarantor_a_id=${userData?._id}`);
  };

  const openEditForm = (formType) => {
    navigate(
      `/guarantor/onboarding?guarantor_a_id=${userData?._id}&action=edit&form=${formType}`
    );
  };

  const handleEditDetails = async (formType) => {
    try {
      setEditGuarantorLoading(true);
      await updateBasicGuarantorAccount({
        guarantor_id: userData?._id,
        fname: guarantor?.fname ?? "",
        lname: guarantor?.lname ?? "",
        email: guarantor?.email ?? "",
        gurantor_relation: userData?.gurantor_relation ?? "",
        step: 1,
      });
      setShowEditConfirmationPrompt(false);
      openEditForm(formType);
    } catch (err) {
      toast.error(_t("failedToUpdateGuarantor"));
    } finally {
      setEditGuarantorLoading(false);
    }
  };

  const sendMail = async (receiverEmail, emailType) => {
    let payload = {
      receiverEmail: receiverEmail,
      senderId: localStorage.getItem("saveduserid"),
      emailType: emailType,
      status: "processing",
      guarantorRequestId: userData?._id,
    };
    await sendUniversalMail(payload)
      .then((res) => {
        toast.success(_t("emailSent"));
      })
      .catch((err) => {
        toast.error(_t("errorSendingMail"));
        console.log(err);
      });
  };

  const handleResendGuarantorRequest = async () => {
    try {
      setResendRequestLoading(true);
      await sendMail(userData?.guarantor_id?.email, "score");
    } catch (err) {
      console.log("Error resend guarantor request");
    } finally {
      setResendRequestLoading(false);
    }
  };

  const deleteGuarantor = async (guarantorId, guarantorRequestId) => {
    try {
      setDeleteGuarantorLoading(true);
      await removeGuarantor(guarantorId, guarantorRequestId);
      loadUser("tenant");
      // they also left the applications where they were this tenant's guarantor
      reloadApplications?.().catch(() => {});
      setShowDeleteConfirmationPrompt(false);
    } catch (err) {
      toast.error(_t("failedToDeleteGuarantor"));
    } finally {
      setDeleteGuarantorLoading(false);
    }
  };

  // Opens the guarantor's profile over the current page, like EarnerCard
  const checkGuarantorProfile = (guarantorId) => {
    const fullPath = location.pathname + location.search;
    navigate(
      `${fullPath}${
        haveValue(location.search) ? "&" : "?"
      }guarantor=${guarantorId}`
    );
  };

  const fullName = `${guarantor?.fname ?? ""} ${guarantor?.lname ?? ""}`.trim();
  const statusData = getGuarantorStatus(userData?.status, guarantor);
  const isCompleted = statusData?.status === "completed";
  const showMenu = (!llShowOnly || isCompleted) && !readOnly;

  const menuItems = [];
  if (isCompleted && guarantor?._id) {
    menuItems.push({
      key: "view",
      label: _t("viewProfile"),
      icon: <Icon name="eye" />,
      onClick: () => {
        setIsDropdown(false);
        onClose?.();
        checkGuarantorProfile(guarantor._id);
      },
    });
  }
  if (userData?.status == "pending") {
    menuItems.push({
      key: "complete",
      label: _t("completeAdding"),
      icon: <Icon name="plus" />,
      onClick: handleCompleteOnboarding,
    });
  }
  if (userData?.status == "processing" || userData?.status == "present") {
    if (userData?.created_guarantor_account === true) {
      menuItems.push({
        key: "edit",
        label: _t("editDetails"),
        icon: <Icon name="edit" />,
        onClick: () => {
          setIsDropdown(false);
          setShowEditConfirmationPrompt(true);
        },
      });
    }
    menuItems.push({
      key: "resend",
      label: _t("resendRequest"),
      icon: <Icon name="send" />,
      onClick: resendRequestLoading
        ? () => {}
        : () => {
            setIsDropdown(false);
            handleResendGuarantorRequest();
          },
    });
  }
  if (!llShowOnly) {
    menuItems.push({
      key: "delete",
      label: _t("delete"),
      icon: <Icon name="trash" />,
      danger: true,
      onClick: () => {
        setIsDropdown(false);
        setShowDeleteConfirmationPrompt(true);
      },
    });
  }

  return (
    <>
      <GuarantorCard
        name={fullName}
        email={guarantor?.email ?? ""}
        onClick={() => isDropdown && setIsDropdown(false)}
        status={<GuarantorStatusBadge statusData={statusData} />}
        menu={
          showMenu ? (
            <GuarantorMenu
              label={_t("more", "More")}
              items={menuItems}
              open={isDropdown}
              onToggle={() => setIsDropdown(!isDropdown)}
            />
          ) : null
        }
      />
      <EditConfirmationPrompt
        isOpen={showEditConfirmationPrompt}
        onClose={() => setShowEditConfirmationPrompt(false)}
        name={
          `${userData?.guarantor_id?.fname ?? ""} ${
            userData?.guarantor_id?.lname ?? ""
          }` ?? ""
        }
        relation={userData?.gurantor_relation ?? ""}
        heading={_t("editDetails")}
        description={_t(
          "editingTheseDetailsWillCancelTheCurrentGuarantorRequest"
        )}
        btn1Text={hasIdentityDetails ? _t("cancel") : _t("editInviteDetails")}
        btn2Text={
          hasIdentityDetails
            ? _t("continue")
            : _t(
                "iCompleteTheProfileMyself",
                "Opens the full guarantor profile form for the tenant to complete"
              )
        }
        btn1Functionality={
          hasIdentityDetails
            ? () => setShowEditConfirmationPrompt(false)
            : () => handleEditDetails("compact")
        }
        btn2Functionality={() => handleEditDetails("full")}
        submitLoading={editGuarantorLoading}
        btn2Variant="primary"
        actionsLayout="stack"
      />
      <DeleteConfiration
        isOpen={showDeleteConfirmationPrompt}
        onClose={() => setShowDeleteConfirmationPrompt(false)}
        name={
          `${userData?.guarantor_id?.fname ?? ""} ${
            userData?.guarantor_id?.lname ?? ""
          }` ?? ""
        }
        relation={userData?.gurantor_relation ?? ""}
        heading={_t("deleteGuarantor")}
        description={_t("areYouSureDoYouWantToDeleteThisGuarantor")}
        btn1Text={_t("noGoBack")}
        btn2Text={_t("yesDelete")}
        btn1Functionality={() => setShowDeleteConfirmationPrompt(false)}
        btn2Functionality={() =>
          deleteGuarantor(userData?.guarantor_id?._id, userData?._id)
        }
        submitLoading={deleteGuarantorLoading}
      />
    </>
  );
};

export default PendingGuarantorPills;
